import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import {
  FontText,
  ThemeIcon,
  FavoriteIcon,
  FontIcon,
  ChapterIcon,
} from '../common';
import { localAnalytics } from '../../utils/analytics';
import { COLORS } from '../../utils/colors';

interface ChapterBottomMenuProps {
  isAuthenticated: boolean;
  isCampaign: boolean;
  isAnonymous: boolean;
  currentChapter: number;
  totalChapters: number;
  fontSize: number;
  onShowChapterList: () => void;
  onFontSizeChange: (size: number) => void;
  onAddToFavorites?: () => void;
  isFavorite?: boolean;
}

export const ChapterBottomMenu: React.FC<ChapterBottomMenuProps> = ({
  isAuthenticated,
  isCampaign,
  isAnonymous,
  currentChapter,
  totalChapters,
  fontSize,
  onShowChapterList,
  onFontSizeChange,
  onAddToFavorites,
  isFavorite = false,
}) => {
  const { t } = useTranslation();
  const { theme, themeMode, toggleTheme } = useTheme();
  const [showFontControls, setShowFontControls] = useState(false);

  const handleFontDecrease = () => {
    if (fontSize > 14) {
      onFontSizeChange(fontSize - 2);
      localAnalytics().logEvent('ChapterFontDecrease', {
        currentFontSize: fontSize,
        newFontSize: fontSize - 2,
      });
    }
  };

  const handleFontIncrease = () => {
    if (fontSize < 24) {
      onFontSizeChange(fontSize + 2);
      localAnalytics().logEvent('ChapterFontIncrease', {
        currentFontSize: fontSize,
        newFontSize: fontSize + 2,
      });
    }
  };

  const toggleFontControls = () => {
    const newState = !showFontControls;
    setShowFontControls(newState);
    localAnalytics().logEvent('ChapterFontControlsToggle', {
      isVisible: newState,
    });
  };

  const handleThemeToggle = () => {
    toggleTheme();
    localAnalytics().logEvent('ChapterThemeToggle', {
      fromTheme: themeMode,
      toTheme: themeMode === 'light' ? 'dark' : 'light',
    });
  };

  const handleChapterListClick = () => {
    onShowChapterList();
    localAnalytics().logEvent('ChapterListIconClick', {
      currentChapter,
      totalChapters,
    });
  };

  const handleFavoriteClick = () => {
    if (onAddToFavorites) {
      onAddToFavorites();
      localAnalytics().logEvent('ChapterFavoriteIconClick', {
        wasFavorite: isFavorite,
        currentChapter,
      });
    }
  };

  const styles: Record<string, React.CSSProperties> = {
    bottomMenu: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: theme.colors.surface,
      borderTop: `1px solid ${theme.colors.overlay}`,
      zIndex: 100,
    },
    progressBar: {
      height: 4,
      backgroundColor: theme.colors.overlay,
      overflow: 'hidden',
    },
    progressFill: {
      height: '100%',
      backgroundColor: theme.colors.accent,
      transition: 'width 0.3s',
    },
    menuContent: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 16px',
      paddingBottom: '12px',
      minHeight: 60,
    },
    fontControlsContainer: {
      position: 'absolute',
      top: -50,
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: theme.colors.surface,
      borderRadius: 16,
      padding: '8px 12px',
      border: `1px solid ${theme.colors.overlay}`,
      display: showFontControls ? 'flex' : 'none',
      alignItems: 'center',
      gap: 8,
      zIndex: 101,
    },
    fontButton: {
      background: 'none',
      border: 'none',
      color: theme.colors.text,
      cursor: 'pointer',
      padding: '4px 6px',
      borderRadius: 4,
      transition: 'opacity 0.2s',
      fontSize: 12,
    },
    fontSizeDisplay: {
      color: theme.colors.text,
      minWidth: 20,
      textAlign: 'center',
    },
    iconButton: {
      border: 'none',
      cursor: 'pointer',
      padding: 8,
      borderRadius: 8,
      backgroundColor: COLORS['element-opacity-8'],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
    },

    hiddenPlaceholder: {
      width: 24,
      height: 24,
      opacity: 0,
    },
  };

  return (
    <div style={styles.bottomMenu}>
      <div style={styles.progressBar}>
        <div
          style={{
            ...styles.progressFill,
            width: `${(currentChapter / totalChapters) * 100}%`,
          }}
        />
      </div>

      <div style={styles.menuContent}>
        <div style={styles.fontControlsContainer}>
          <button
            style={{
              ...styles.fontButton,
              opacity: fontSize <= 14 ? 0.5 : 1,
              cursor: fontSize <= 14 ? 'not-allowed' : 'pointer',
            }}
            onClick={handleFontDecrease}
            disabled={fontSize <= 14}
          >
            <FontText>{t('font_decrease')}</FontText>
          </button>
          <FontText small style={styles.fontSizeDisplay}>
            {fontSize}
          </FontText>
          <button
            style={{
              ...styles.fontButton,
              opacity: fontSize >= 24 ? 0.5 : 1,
              cursor: fontSize >= 24 ? 'not-allowed' : 'pointer',
            }}
            onClick={handleFontIncrease}
            disabled={fontSize >= 24}
          >
            <FontText>{t('font_increase')}</FontText>
          </button>
        </div>

        {!isCampaign || (isAuthenticated && !isAnonymous) ? (
          <button style={styles.iconButton} onClick={handleChapterListClick}>
            <ChapterIcon color={theme.colors.text} />
          </button>
        ) : (
          <div style={styles.hiddenPlaceholder} />
        )}

        <button style={styles.iconButton} onClick={toggleFontControls}>
          <FontIcon color={theme.colors.text} />
        </button>

        <button style={styles.iconButton} onClick={handleThemeToggle}>
          <ThemeIcon color={theme.colors.text} />
        </button>

        {isAuthenticated && !isAnonymous && onAddToFavorites ? (
          <button style={styles.iconButton} onClick={handleFavoriteClick}>
            <FavoriteIcon color={theme.colors.text} isActive={isFavorite} />
          </button>
        ) : (
          <div style={styles.hiddenPlaceholder} />
        )}
      </div>
    </div>
  );
};
